import { matchPath } from 'react-router-dom';
import { ROUTES, ROUTE_PARAMS, PREDICT_PRODUCT_SLUG } from '../../app-core';
import {
  getInternalProductConsolidationRoute,
  getInternalProductRoute,
  getProductConsolidationRoute,
  getProductRoute
} from '../../organisations';
import { interpolatePathParams, joinRouteMatchPath, joinRouteMatchUrl } from '../../path';

// ***************
// * Route paths *
// ***************

export const getReportingDocumentLibraryRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.DOCUMENT_LIBRARY);
};
export const getReportingDocumentLibraryAwaitingReviewRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.DOCUMENT_LIBRARY, ROUTES.AWAITING_REVIEW);
};
export const getReportingDocumentLibraryExportedPDFsRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.DOCUMENT_LIBRARY, ROUTES.EXPORTED_PDFS);
};
export const getReportingDocumentLibraryScheduledReportsRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.DOCUMENT_LIBRARY, ROUTES.SCHEDULED_REPORTS);
};
export const getReportingDocumentLibrarySentPDFsRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.DOCUMENT_LIBRARY, ROUTES.SENT_PDFS);
};
export const getReportingEditReportRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING_EDIT);
};
export const getReportingEditTemplateRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORT_TEMPLATE_EDIT);
};
export const getReportingInternalReportRenderRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getInternalProductConsolidationRoute() : getInternalProductRoute();
  return joinRouteMatchPath('', base, ROUTES.REPORTING, ROUTES.REPORTING_BY_ID);
};
export const getReportingRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING);
};
export const getReportingReportsListRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.REPORTS_LIST);
};
export const getReportingReportSchedulingRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.REPORT_SCHEDULING);
};
export const getReportingExportedPdfsRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.EXPORTED_PDFS);
};
export const getReportingReportTemplatesRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING, ROUTES.REPORT_TEMPLATES);
};
export const getReportingViewReportRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORTING_VIEW);
};
export const getReportingViewTemplateRoutePath = (isConsolidation = false) => {
  const base = isConsolidation ? getProductConsolidationRoute() : getProductRoute();
  return joinRouteMatchUrl('', base, ROUTES.REPORT_TEMPLATE_VIEW);
};

// *************
// * Page URLs *
// *************

// Why is this here?
export const getHomePageUrl = (workspaceId: string, organisationId: string) => {
  return `${interpolatePathParams(getProductRoute(), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  })}${ROUTES.HOME}`;
};

export const getReportingDocumentLibraryAwaitingReviewUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingDocumentLibraryAwaitingReviewRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingDocumentLibraryExportedPDFsUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingDocumentLibraryExportedPDFsRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingDocumentLibraryScheduledReportsUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingDocumentLibraryScheduledReportsRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingDocumentLibrarySentPDFsUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingDocumentLibrarySentPDFsRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingEditReportUrl = (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingEditReportRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG,
    [ROUTE_PARAMS.REPORT_ID]: reportId
  });

export const getReportingEditTemplateUrl = (
  workspaceId: string,
  organisationId: string,
  id: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingEditTemplateRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG,
    [ROUTE_PARAMS.TEMPLATE_ID]: id
  });

export const getReportingInternalReportRenderUrl = (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  userId: string,
  isConsolidation?: boolean
): string => {
  const url = interpolatePathParams(getReportingInternalReportRenderRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.REPORT_ID]: reportId
  });
  return `${url}?${ROUTE_PARAMS.USER_ID}=${userId}`;
};
export const getReportingUrl = (workspaceId: string, organisationId: string, isConsolidation?: boolean): string =>
  interpolatePathParams(getReportingRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingReportsListUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingReportsListRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingReportSchedulingUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingReportSchedulingRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingExportedPdfsUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingExportedPdfsRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

export const getReportingReportTemplatesUrl = (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingReportTemplatesRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG
  });

// Explicit view URLs keep reports in view mode even when the user has edit access.
export const getReportingViewReportUrl = (
  workspaceId: string,
  organisationId: string,
  reportId: string,
  isConsolidation?: boolean
): string =>
  interpolatePathParams(getReportingViewReportRoutePath(isConsolidation), {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG,
    [ROUTE_PARAMS.REPORT_ID]: reportId
  });

// ******************
// * Route matchers *
// ******************

export const matchAnyReportingInternalRenderReportUrl = (url: string) => {
  return matchReportingInternalRenderReportUrl(url) || matchReportingInternalRenderReportUrl(url, true);
};
export const matchAnyReportingViewReportUrl = (url: string) => {
  return !!matchReportingViewReportUrl(url) || matchReportingViewReportUrl(url, true);
};
export const matchAnyReportingViewTemplateUrl = (url: string) => {
  return !!matchReportingViewTemplateUrl(url) || matchReportingViewTemplateUrl(url, true);
};

export const matchReportingDocumentLibraryAwaitingReviewUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingDocumentLibraryAwaitingReviewRoutePath(isConsolidation) }, url);
};
export const matchReportingDocumentLibraryExportedPDFsUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingDocumentLibraryExportedPDFsRoutePath(isConsolidation) }, url);
};
export const matchReportingDocumentLibraryScheduledReportsUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingDocumentLibraryScheduledReportsRoutePath(isConsolidation) }, url);
};
export const matchReportingDocumentLibrarySentPDFsUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingDocumentLibrarySentPDFsRoutePath(isConsolidation) }, url);
};
export const matchReportingEditReportUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingEditReportRoutePath(isConsolidation) }, url);
};
export const matchReportingEditTemplateUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingEditTemplateRoutePath(isConsolidation) }, url);
};
export const matchReportingInternalRenderReportUrl = (url: string, isConsolidation?: boolean) => {
  const match = matchPath({ path: getReportingInternalReportRenderRoutePath(isConsolidation) }, url);
  return !!match;
};
export const matchReportingViewReportUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingViewReportRoutePath(isConsolidation) }, url);
};
export const matchReportingViewTemplateUrl = (url: string, isConsolidation?: boolean) => {
  return matchPath({ path: getReportingViewTemplateRoutePath(isConsolidation) }, url);
};
